import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { combineStrings } from 'assets/utils/data/String';
import { keys } from 'lodash';
import Claim from 'models/claims/claim/Model.Claim';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import ClaimStatusCode from 'models/enums/ClaimStatusCode';
import { useMemo } from 'react';

export default function useClaimStatuses() {
  const { lang } = getLocales();
  
  function mapStatus(
    status: string,
    entity?: 'claim' | 'claimJob'
  ): { id: string; title: string; subtitle: string } | undefined {
    if (entity) {
      const claimExcludes = [ClaimStatusCode.PAID_CLOSED_PAID, ClaimStatusCode.CANCELED_VOID];
      const claimJobExcludes = [
        ClaimJobStatusCode.PAID_CLOSED_PAID,
        ClaimJobStatusCode.CANCELED_VOID,
        ClaimJobStatusCode.OPEN_APPROVED_TO_PAY_BY_ACCOUNTING,
        ClaimJobStatusCode.OPEN_APPROVED_INVOICE_RECEIVED,
        ClaimJobStatusCode.OPEN_APPROVED_INVOICE_REVIEW_SERVICE,
        ClaimJobStatusCode.OPEN_PAID_PAYMENT_SENT,
      ];

      if (
        (entity === 'claim' && claimExcludes.includes(status as ClaimStatusCode)) ||
        (entity === 'claimJob' && claimJobExcludes.includes(status as ClaimJobStatusCode))
      )
        return undefined;
    }
    const [primary, secondary, reason] = (status ? lang[status] : status).split('_');
    const mainStatus = combineStrings(' - ', primary, secondary);
    return {
      id: status,
      title: reason ?? mainStatus,
      subtitle: mainStatus,
    };
  }

  const claimStatusInfoApi = useApi({ action: Claim.getStatusFlowAndRequirements });
  const claimStatusInfo = claimStatusInfoApi.payload;
  const claimStatuses = useMemo(() => keys(claimStatusInfo).map((status) => mapStatus(status)), [claimStatusInfo]);
  const automationClaimStatuses = useMemo(
    () =>
      keys(claimStatusInfo)
        .map((status) => mapStatus(status, 'claim'))
        .filter(Boolean),
    [claimStatusInfo]
  );

  const claimJobStatusInfoApi = useApi({ action: ClaimJob.getStatusFlowAndRequirements });
  const claimJobStatusInfo = claimJobStatusInfoApi.payload;
  const claimJobStatuses = useMemo(
    () => keys(claimJobStatusInfo).map((status) => mapStatus(status)),
    [claimJobStatusInfo]
  );
  const automationClaimJobStatuses = useMemo(
    () =>
      keys(claimJobStatusInfo)
        .map((status) => mapStatus(status, 'claimJob'))
        .filter(Boolean),
    [claimJobStatusInfo]
  );

  return {
    claimStatuses,
    claimJobStatuses,
    automationClaimStatuses,
    automationClaimJobStatuses,
  };
}
